/*
 * The drawing tools' arithmetic (เครื่องมือ, src/ui/drawTools.ts), free of DOM/Fabric for the tests
 * (tests/draw-tools.test.ts): the shapes' corner points, a dragged box, line dash patterns and arrow heads, the pens.
 * The same arrow geometry is drawn on screen (src/editor/arrows.ts), in pictures and in the print PDF
 * (src/print/pdf.ts) — one source, so they always match.
 */

export interface Pt { x: number; y: number }

/* ---------- รูปทรง ---------- */

export type ShapeId = 'square' | 'rounded' | 'circle' | 'triangle' | 'triangleDown' | 'diamond' | 'pentagon' | 'hexagon' | 'octagon';
export const SHAPES: Array<{ id: ShapeId; label: string }> = [
  { id: 'square', label: 'สี่เหลี่ยม' }, { id: 'rounded', label: 'สี่เหลี่ยมมุมมน' }, { id: 'circle', label: 'วงกลม' },
  { id: 'triangle', label: 'สามเหลี่ยม' }, { id: 'triangleDown', label: 'สามเหลี่ยมกลับหัว' }, { id: 'diamond', label: 'ข้าวหลามตัด' },
  { id: 'pentagon', label: 'ห้าเหลี่ยม' }, { id: 'hexagon', label: 'หกเหลี่ยม' }, { id: 'octagon', label: 'แปดเหลี่ยม' }
];

/**
 * The corners of a regular shape with `n` sides inside a `w` × `h` box, starting at the top
 * (an octagon is turned so its top side is flat, as people expect).
 */
export function polygonPoints(n: number, w: number, h: number): Pt[] {
  const turn = n === 8 ? Math.PI / 8 : n === 6 ? Math.PI / 6 : 0;
  const raw = Array.from({ length: n }, (_, k) => {
    const a = -Math.PI / 2 + turn + (2 * Math.PI * k) / n;
    return { x: Math.cos(a), y: Math.sin(a) };
  });
  // stretch to fill the box exactly (a regular pentagon is not as tall as it is wide)
  const minX = Math.min(...raw.map(p => p.x)), maxX = Math.max(...raw.map(p => p.x));
  const minY = Math.min(...raw.map(p => p.y)), maxY = Math.max(...raw.map(p => p.y));
  return raw.map(p => ({ x: ((p.x - minX) / (maxX - minX)) * w, y: ((p.y - minY) / (maxY - minY)) * h }));
}

/** sides of the shapes made as polygons */
export const POLYGON_SIDES: Partial<Record<ShapeId, number>> = { diamond: 4, pentagon: 5, hexagon: 6, octagon: 8 };

/** the box between where a drag started and where it is (any direction); `square` keeps it square (Shift) */
export function dragBox(a: Pt, b: Pt, square = false): { x: number; y: number; w: number; h: number } {
  let w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
  if (square) w = h = Math.max(w, h);
  return { x: b.x >= a.x ? a.x : a.x - w, y: b.y >= a.y ? a.y : a.y - h, w, h };
}

/** a press that moved less than this (screen px) is a tap: the usual size is placed where it was tapped */
export const TAP_MOVE_PX = 8;

/* ---------- เส้น ---------- */

export type DashStyle = 'solid' | 'long' | 'short' | 'dots';
export const DASH_LABEL: Record<DashStyle, string> = { solid: 'เส้นทึบ', long: 'เส้นประยาว', short: 'เส้นประสั้น', dots: 'จุด' };

/** Fabric's strokeDashArray for a style at line width `sw` (dots need a round cap, set with them) */
export function dashArray(style: DashStyle, sw: number): number[] | null {
  const w = Math.max(1, sw);
  if (style === 'long') return [w * 4, w * 2.5];
  if (style === 'short') return [w * 1.5, w * 1.5];
  if (style === 'dots') return [0.01, w * 2];
  return null;
}

/** which style a line's dash pattern is (for the toolbar) */
export function dashStyleOf(dash: unknown, sw: number): DashStyle {
  if (!Array.isArray(dash) || !dash.length) return 'solid';
  const w = Math.max(1, sw);
  if (dash[0] < 0.1) return 'dots';
  return dash[0] / w > 2.5 ? 'long' : 'short';
}

export type ArrowEnds = 'none' | 'end' | 'start' | 'both';
export const ARROW_LABEL: Record<ArrowEnds, string> = { none: 'ไม่มีหัวลูกศร', end: 'หัวลูกศรปลาย →', start: 'หัวลูกศรต้น ←', both: 'หัวลูกศรสองทาง ↔' };
export const arrowEndsOf = (o: { arrowStart?: unknown; arrowEnd?: unknown }): ArrowEnds =>
  o.arrowStart && o.arrowEnd ? 'both' : o.arrowStart ? 'start' : o.arrowEnd ? 'end' : 'none';

/** length and width of a head for a line `sw` wide */
export const headSize = (sw: number) => ({ len: Math.max(10, sw * 3.5), wid: Math.max(9, sw * 3) });

/**
 * A line with heads, in the same units as its end points. Each head is a triangle whose tip is the end point, and the
 * line and the heads never overlap — with a see-through line an overlap shows as a darker band:
 *  - a solid line is stroked with flat ends (`cap: 'butt'`) that stop exactly at each head's base; the line's own
 *    round/square end, at an end with no head, is drawn as a filled shape just past that end (in `heads`)
 *  - a dashed line keeps its own ends (dots need round ones) and stops so they reach the base, no further
 * A line too short for its heads keeps them, overlapping.
 */
export function arrowGeometry(p1: Pt, p2: Pt, sw: number, start: boolean, end: boolean, cap = 'butt', dashed = false):
  { a: Pt; b: Pt; heads: Pt[][]; cap: string } {
  const dx = p2.x - p1.x, dy = p2.y - p1.y, len = Math.hypot(dx, dy);
  if (len === 0 || (!start && !end)) return { a: p1, b: p2, heads: [], cap };
  const ux = dx / len, uy = dy / len, { len: hl, wid } = headSize(sw);
  const head = (tip: Pt, dir: 1 | -1): Pt[] => {
    const bx = tip.x - dir * ux * hl, by = tip.y - dir * uy * hl;
    return [tip, { x: bx - uy * wid / 2, y: by + ux * wid / 2 }, { x: bx + uy * wid / 2, y: by - ux * wid / 2 }];
  };
  const reach = dashed && cap !== 'butt' ? sw / 2 : 0;           // how far a dash's own end sticks out
  const cut = Math.min(hl + reach, len / 2);
  // an end with no head on a solid line: its round/square end, as a shape just past the end point
  const ownEnd = (at: Pt, dir: 1 | -1): Pt[] => {
    const fx = dir * ux, fy = dir * uy, r = sw / 2;               // forward, out of the line
    if (cap === 'square') return [
      { x: at.x - fy * r, y: at.y + fx * r }, { x: at.x - fy * r + fx * r, y: at.y + fx * r + fy * r },
      { x: at.x + fy * r + fx * r, y: at.y - fx * r + fy * r }, { x: at.x + fy * r, y: at.y - fx * r }];
    const a0 = Math.atan2(fx, -fy);                                  // from one side of the line, round the front, to the other
    return Array.from({ length: 17 }, (_, k) => { const t = a0 - (Math.PI * k) / 16; return { x: at.x + Math.cos(t) * r, y: at.y + Math.sin(t) * r }; });
  };
  const capShapes = !dashed && cap !== 'butt' ? [...(start ? [] : [ownEnd(p1, -1)]), ...(end ? [] : [ownEnd(p2, 1)])] : [];
  return {
    a: start ? { x: p1.x + ux * cut, y: p1.y + uy * cut } : p1,
    b: end ? { x: p2.x - ux * cut, y: p2.y - uy * cut } : p2,
    heads: [...(start ? [head(p1, -1)] : []), ...(end ? [head(p2, 1)] : []), ...capShapes],
    cap: dashed ? cap : 'butt'
  };
}

/* ---------- ปากกา ---------- */

export type PenKind = 'pen' | 'marker' | 'highlighter';
export const PENS: Record<PenKind, { label: string; width: number; color: string; alpha: number }> = {
  pen: { label: 'ปากกา', width: 4, color: '#1d1a2b', alpha: 1 },
  marker: { label: 'มาร์กเกอร์', width: 14, color: '#e5484d', alpha: 1 },
  highlighter: { label: 'ไฮไลต์', width: 28, color: '#ffd60a', alpha: 0.4 }
};

/** a #rrggbb colour at `alpha` as rgba() (the highlighter is see-through) */
export function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  if (alpha >= 1) return `#${m[1]}${m[2]}${m[3]}`.toLowerCase();
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}

/* ---------- โน้ตแปะ (6ก: on screen only, never in a file) ---------- */

export const NOTE_COLORS = ['#ffe066', '#ffc078', '#ff8787', '#74c0fc', '#8ce99a', '#b197fc'];

/** a note is kept with the work but left out of every output (src/editor/pageFormat.ts withoutNotes) */
export const isNote = (o: { note?: unknown }) => o.note === true;
